"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useTranslation } from "@sajilni/localization";
import { cn } from "@sajilni/utils/cn";
import {
  authAudienceTypeParam,
  buildAuthFlowHref,
  getPreservedAuthParams,
  type AuthAudience,
} from "@/lib/auth-flow-params";

type AuthAudienceToggleProps = {
  current: AuthAudience;
  basePath: "/login" | "/signup";
};

export default function AuthAudienceToggle({
  current,
  basePath,
}: AuthAudienceToggleProps) {
  const { t } = useTranslation("sajilni2.0");
  const searchParams = useSearchParams();
  const preserved = getPreservedAuthParams(searchParams);

  const organizerHref = buildAuthFlowHref(basePath, {
    ...preserved,
    type: authAudienceTypeParam("organizer"),
  });
  const attendeeHref = buildAuthFlowHref(basePath, {
    ...preserved,
    type: authAudienceTypeParam("attendee"),
  });

  const options: { audience: AuthAudience; href: string; label: string }[] = [
    {
      audience: "organizer",
      href: organizerHref,
      label: t("Sign In as Organizer"),
    },
    {
      audience: "attendee",
      href: attendeeHref,
      label: t("Sign In as Attendee"),
    },
  ];

  return (
    <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
      {options.map(({ audience, href, label }) => (
        <Link
          key={audience}
          href={href}
          className={cn(
            "rounded-md px-3 py-1.5 text-xs font-medium transition-colors sm:text-sm",
            current === audience
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          )}
        >
          {label}
        </Link>
      ))}
    </div>
  );
}
